import { useCallback, useMemo, useState } from "react";
import { PlateProvider } from "@udecode/plate-core";
import { clsx } from "clsx";
import { useDebouncedCallback } from "use-debounce";

import { useLabels } from "~/hooks/useLabels";
import { useNetworkState } from "~/hooks/useNetworkState";
import { logger } from "~/utils/logging";

import { serialize } from "./serialize";
import { deserialize } from "./deserialize";
import { plugins } from "./plugins";
import { TextEditorToolbar } from "./TextEditorToolbar";
import { TextEditorCharacterCount } from "./TextEditorCharacterCount";
import { TextEditorSaveIndicator } from "./TextEditorSaveIndicator";
import { TextEditorStatusBar } from "./TextEditorStatusBar";
import { TextEditorEditor } from "./TextEditorEditor";
import { DEFAULT_LABELS } from "./constants";

import "./TextEditor.css";

import type { ChangeEvent } from "react";
import type { EditorValue } from "./types";
import type { TextEditorControlIntent } from "./TextEditorControl";

export interface TextEditorChangeEvent {
  value: string;
}

export interface TextEditorSaveEvent extends TextEditorChangeEvent {}

export interface TextEditorProps {
  useSimpleEditor?: boolean;
  autoFocus?: boolean;
  autoSave?: boolean;
  id?: string;
  initialValue?: string;
  layout?: "normal" | "fill";
  limit?: number;
  placeholder?: string;
  onBlur?: () => void;
  onChange?: (event: TextEditorChangeEvent) => void;
  onFocus?: () => void;
  onSave?: (event: TextEditorSaveEvent) => Promise<void>;
  onDraftSave?: (event: TextEditorSaveEvent) => Promise<void>;
}

export enum TextEditorSaveState {
  loading,
  ready,
  pending,
  successful,
  failed,
}

export function TextEditor({
  useSimpleEditor = false,
  autoFocus,
  autoSave,
  id,
  initialValue,
  layout,
  limit,
  placeholder = DEFAULT_LABELS["textEditor.placeholder"],
  onBlur,
  onChange,
  onFocus,
  onSave,
  onDraftSave,
}: TextEditorProps) {
  const { t } = useLabels();
  const { online } = useNetworkState();

  const [value, setValue] = useState(initialValue ?? "");
  const [isLimitReached, setIsLimitReached] = useState(false);
  const [autoSaveState, setAutoSaveState] = useState<TextEditorSaveState>(
    TextEditorSaveState.loading,
  );
  const [saveState, setSaveState] = useState<TextEditorSaveState>(
    TextEditorSaveState.loading,
  );
  const [keyboardIntent, setKeyboardIntent] =
    useState<TextEditorControlIntent>("hideKeyboard");
  const [simpleCharacterCount, setSimpleCharacterCount] = useState<number>(
    initialValue?.replace(/\n/g, "").length ?? 0,
  );

  const className = clsx("TextEditor", {
    [`TextEditor--layout-${layout}`]: layout,
  });

  const canSubmit =
    online && Boolean(onSave) && Boolean(value.length) && !isLimitReached;

  const parsedInitialValue = useMemo(() => {
    try {
      return deserialize(initialValue);
    } catch (error) {
      logger.error("parsedInitialValue failed", error);
    }
  }, [initialValue]);

  const saveDraft = useDebouncedCallback(async (value: string) => {
    const isSaving = saveState === TextEditorSaveState.pending;

    if (!onDraftSave || !autoSave || isSaving) return;

    setAutoSaveState(TextEditorSaveState.pending);

    await onDraftSave?.({ value })
      .then(() => {
        setAutoSaveState(TextEditorSaveState.successful);
      })
      .catch(() => {
        setAutoSaveState(TextEditorSaveState.failed);
      });
  }, 500);

  const handleChange = useDebouncedCallback((editorValue: EditorValue) => {
    const newValue = serialize(editorValue);
    setValue(newValue);
    onChange?.({ value: newValue });

    if (autoSave && newValue && newValue !== initialValue) {
      saveDraft(newValue);
    }
  }, 500);

  const saveSimpleDraft = useDebouncedCallback((value: string) => {
    setValue(value);
    onChange?.({ value });

    if (autoSave && value && value !== initialValue) {
      saveDraft(value);
    }
  }, 500);

  const handleSimpleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const value = event.target?.value ?? "";
      setSimpleCharacterCount(value.replace(/\n/g, "").length);
      saveSimpleDraft(value);
    },
    [setSimpleCharacterCount, saveSimpleDraft],
  );

  const handleLimitStateChange = useCallback((isLimitReached: boolean) => {
    setIsLimitReached(isLimitReached);
  }, []);

  const handleSave = useCallback(async () => {
    setSaveState(TextEditorSaveState.pending);

    await onSave?.({ value })
      .then(() => {
        setSaveState(TextEditorSaveState.successful);
      })
      .catch(() => {
        setSaveState(TextEditorSaveState.failed);
      });

    setSaveState(TextEditorSaveState.ready);
  }, [onSave, value]);

  const handleBlur = useCallback(() => setKeyboardIntent("showKeyboard"), []);

  const handleFocus = useCallback(() => setKeyboardIntent("hideKeyboard"), []);

  const renderedStatusBar = useMemo(() => {
    if (!(limit || onDraftSave)) return null;

    return (
      <TextEditorStatusBar>
        {limit && (
          <TextEditorCharacterCount
            limit={limit}
            count={useSimpleEditor ? simpleCharacterCount : undefined}
            onStateChange={handleLimitStateChange}
          />
        )}

        {onDraftSave && <TextEditorSaveIndicator status={autoSaveState} />}
      </TextEditorStatusBar>
    );
  }, [
    limit,
    onDraftSave,
    useSimpleEditor,
    simpleCharacterCount,
    handleLimitStateChange,
    autoSaveState,
  ]);

  const simpleEditor = (
    <>
      <div className={className}>
        <textarea
          className="TextEditorEditor__simple-textarea"
          placeholder={placeholder ?? t("textEditor.placeholder.note")}
          defaultValue={initialValue ?? ""}
          onChange={handleSimpleChange}
        />

        {renderedStatusBar}

        <TextEditorToolbar
          useSimpleEditor={useSimpleEditor}
          id={id}
          canSubmit={canSubmit}
          keyboardIntent={keyboardIntent}
          isSaving={saveState === TextEditorSaveState.pending}
          onSave={handleSave}
        />
      </div>
    </>
  );

  const plateEditor = (
    <PlateProvider
      id={id}
      initialValue={parsedInitialValue}
      plugins={plugins}
      onChange={handleChange}
    >
      <div className={className}>
        <TextEditorEditor
          autoFocus={autoFocus}
          id={id}
          placeholder={placeholder ?? t("textEditor.placeholder.note")}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />

        {renderedStatusBar}

        <TextEditorToolbar
          id={id}
          canSubmit={canSubmit}
          keyboardIntent={keyboardIntent}
          isSaving={saveState === TextEditorSaveState.pending}
          onSave={handleSave}
        />
      </div>
    </PlateProvider>
  );

  return <>{useSimpleEditor ? simpleEditor : plateEditor}</>;
}
